import IconTextCardListed from "../ui/icon-text-card-listed";
import { cn, withUrl } from "@/lib/utils";
import LINKS from "@/lib/links";
import { getIcon, IconName } from "@/lib/get-icon";

export interface ServicesWeOfferProps {
  id?: string;
  className?: string;
  heading?: string;
  subheading?: string;
  services?: ServiceOfferData[];
}

export type ServiceOfferData = {
  icon: IconName;
  title?: string;
  items?: {
    name: string;
    href: string;
    prefetch?: boolean;
  }[];
  cta?: {
    href: string;
    text: string;
    ariaLabel?: string;
    className?: string;
    prefetch?: boolean;
    color?: "orange" | "teal";
  };
};

export const SERVICES_WE_OFFER_DATA_V2: ServiceOfferData[] = [
  {
    icon: "staffing-icon",
    title: "Facility Staffing",
    items: [
      {
        name: LINKS.longTermCareStaffing.title,
        href: LINKS.longTermCareStaffing.href,
      },
      {
        name: LINKS.medicalSettings.title,
        href: LINKS.medicalSettings.href,
      },
      {
        name: "Extra Support for Families",
        href: LINKS.extraSupportToOfferFamilies.href,
      },
      {
        name: "One-on-One Hospital Sitter",
        href: LINKS.hospitalSitterStaffing.href,
      },
    ],
    cta: {
      href: withUrl(LINKS.medicalStaffing.href),
      text: "See Staffing Solutions",
      color: "orange",
      className:
        "text-body-3-400 font-montserrat font-normal mt-0 px-2 py-1 lg:text-subheading-4-600",
      ariaLabel: "Go to Medical Staffing",
    },
  },
  {
    icon: "services-for-families-icon",
    title: "Services for Families",
    items: [
      {
        name: LINKS.homeCare.title,
        href: LINKS.homeCare.href,
      },
      {
        name: LINKS.hospitalSitterService.title,
        href: LINKS.hospitalSitterService.href,
      },
      {
        name: LINKS.longTermCareService.title,
        href: LINKS.longTermCareService.href,
      },
      {
        name: "Private Duty Medical Nurse",
        href: LINKS.privateDutyMedicalCare.href,
      },
      {
        name: LINKS.specializedCare.title,
        href: LINKS.specializedCare.href,
      },
      {
        name: LINKS.diseaseSpecialties.title,
        href: LINKS.diseaseSpecialties.href,
      },
    ],
    cta: {
      href: withUrl(LINKS.servicesForFamilies.href),
      text: "View Care Services",
      color: "orange",
      className:
        "text-body-3-400 font-montserrat font-normal mt-0 px-2 py-1 lg:text-subheading-4-600",
      ariaLabel: "Go to Services for Families",
    },
  },
  {
    icon: "resources-for-families-icon",
    title: "Resources for Families",
    items: [
      {
        name: LINKS.blogFamilies.title,
        href: LINKS.blogFamilies.href,
        prefetch: !LINKS.blogFamilies.doNotPrefetch,
      },
      {
        name: LINKS.classesFamilies.title,
        href: LINKS.classesFamilies.href,
        prefetch: !LINKS.classesFamilies.doNotPrefetch,
      },
      {
        name: LINKS.eventsFamilies.title,
        href: LINKS.eventsFamilies.href,
        prefetch: !LINKS.eventsFamilies.doNotPrefetch,
      },
      {
        name: LINKS.helpCenter.title,
        href: LINKS.helpCenter.href,
      },
    ],
    cta: {
      href: withUrl(LINKS.blogFamilies.href),
      text: "Discover Resources",
      color: "orange",
      className:
        "text-body-3-400 font-montserrat font-normal mt-0 px-2 py-1 lg:text-subheading-4-600",
      ariaLabel: "Go to Resources",
    },
  },
  {
    icon: "resources-for-professionals-icon",
    title: "Resources for Professionals",
    items: [
      {
        name: LINKS.blogProfessionals.title,
        href: LINKS.blogProfessionals.href,
        prefetch: !LINKS.blogProfessionals.doNotPrefetch,
      },
      {
        name: LINKS.classesProfessionals.title,
        href: LINKS.classesProfessionals.href,
        prefetch: !LINKS.classesProfessionals.doNotPrefetch,
      },
      {
        name: LINKS.eventsProfessionals.title,
        href: LINKS.eventsProfessionals.href,
        prefetch: !LINKS.eventsProfessionals.doNotPrefetch,
      },
      {
        name: LINKS.ciVR.title,
        href: LINKS.ciVR.href,
      },
      {
        name: LINKS.ciION.title,
        href: LINKS.ciION.href,
      },
    ],
    cta: {
      href: withUrl(LINKS.blogProfessionals.href),
      text: "Discover Resources",
      color: "orange",
      className:
        "text-body-3-400 font-montserrat font-normal mt-0 px-2 py-1 lg:text-subheading-4-600",
      ariaLabel: "Go to Resources",
    },
  },
];

const defaultProps: ServicesWeOfferProps = {
  heading: "What We Offer",
  subheading: "We provide more than customized care and staffing solutions.",
  services: SERVICES_WE_OFFER_DATA_V2,
};

const ServicesWeOffer: React.FC<ServicesWeOfferProps> = ({
  id = "services-we-offer",
  className,
  heading,
  subheading,
  services,
}) => {
  heading = heading ?? defaultProps.heading;
  subheading = subheading ?? defaultProps.subheading;
  services = services ?? defaultProps.services;

  return (
    <section
      id={id}
      aria-label={id}
      className={cn(
        "mx-auto flex max-w-lg flex-col gap-6 bg-white px-4 py-8 md:gap-8 md:px-6 md:py-10 lg:gap-10 lg:px-3 lg:py-12",
        className
      )}
    >
      <div className="flex flex-col gap-2 text-center md:gap-3">
        <h2 className="font-montserrat text-heading-4 text-primary-500 md:text-heading-3 lg:text-heading-2">
          {heading}
        </h2>
        <p className="font-montserrat text-subheading-3-400 text-neutral-500 lg:text-subheading-2">
          {subheading}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4 md:gap-8 lg:gap-16">
        {services?.map((service, index) => (
          <IconTextCardListed
            key={`${service.title}-${index}`}
            className="col-span-1 px-2 py-4 lg:w-[266px] lg:px-2 lg:py-8"
            titleClassName="mt-0 pt-2 lg:pt-4"
            itemsGroupClassName="hidden lg:block mb-4"
            icon={getIcon({
              name: service.icon,
              className: "size-10 lg:size-20",
            })}
            title={service.title}
            items={service.items}
            cta={service.cta}
            linkBox
          />
        ))}
      </div>
    </section>
  );
};

export default ServicesWeOffer;
